import { Divider, Grid, Group, Stack, Text } from "@mantine/core"
import { IconNavion, Card, Accordion, ActionIcon, Menu } from "@tfg/ui"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { ConnectionCard } from "~/pages/Brand/Suppliers/List/components/ConnectionCard.tsx"
import { ContactCard } from "~/pages/Brand/Suppliers/List/components/ContactCard.tsx"
import { ProductionSiteCard } from "~/pages/Brand/Suppliers/List/components/ProductionSiteCard.tsx"
import { SupplierListProgressBar } from "~/pages/Brand/Suppliers/List/components/SupplierListProgressBar.tsx"

interface Props {
  contact: LibraryResponses.SupplierDetails
  supplier: LibraryResponses.SupplierMonitoring
  openModal?: () => void
  setContactModal?: Dispatch<SetStateAction<LibraryResponses.SupplierDetails>>
  type: "supplier" | "subSupplier"
  setTransferType?: Dispatch<
    SetStateAction<"informationsRequests" | "certiticates">
  >
}

export const SupplierListMonitoringContactItem = ({
  contact,
  supplier,
  openModal,
  setContactModal,
  type,
  setTransferType,
}: Props) => {
  const { t } = useTranslation("suppliers")
  const onTransfer = () => {
    setContactModal && setContactModal(contact)
    openModal && openModal()
  }

  const remainingInformationRequests =
    contact.traceability.totalRequests - contact.traceability.completedRequests
  const isRemainingRequestToTransfert =
    remainingInformationRequests > 0 && contact.isDirectSupplier

  const remainingCertificateRequests =
    contact.scopeCertificate.totalRequests +
    contact.txCertificate.totalRequests -
    (contact.scopeCertificate.completedRequests +
      contact.txCertificate.completedRequests)

  const irRemainingCertificateRequestToTransfer =
    remainingCertificateRequests > 0 && contact.isDirectSupplier

  return (
    <Card>
      <Stack>
        <Grid align="center" columns={48}>
          <Grid.Col span="auto">
            <Group justify="space-between">
              <ContactCard contact={contact} />
            </Group>
          </Grid.Col>
          <Grid.Col span={13}>
            <ConnectionCard contact={contact} />
          </Grid.Col>
          <Grid.Col span={8}>
            <SupplierListProgressBar ratio={supplier.supplierInfo.ratio} />
          </Grid.Col>
          <Grid.Col span={14}>
            <Grid columns={14}>
              <Grid.Col span={12}>
                <ReqStats contact={contact} />
              </Grid.Col>
              {type === "supplier" && (
                <Grid.Col span={2} style={{ alignSelf: "flex-start" }}>
                  <SupplierSettings
                    actions={[
                      {
                        label: t("supplier.transferContact", {
                          count: remainingInformationRequests,
                        }),
                        disabled: !isRemainingRequestToTransfert,
                        onClick: () => {
                          onTransfer()
                          setTransferType &&
                            setTransferType("informationsRequests")
                        },
                      },
                      {
                        label: t("supplier.transferContactCertificates", {
                          count: remainingCertificateRequests,
                        }),
                        disabled: !irRemainingCertificateRequestToTransfer,
                        onClick: () => {
                          onTransfer()
                          setTransferType && setTransferType("certiticates")
                        },
                      },
                    ]}
                  />
                </Grid.Col>
              )}
            </Grid>
          </Grid.Col>
        </Grid>
        {supplier.productionSites.length > 0 && (
          <Accordion>
            <Accordion.Item value="default">
              <Accordion.Control>
                <Text fw={600}>
                  {t("list.productionSites", {
                    count: supplier.productionSites.length,
                  })}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                {supplier.productionSites.map((productionSite, index) => {
                  return (
                    <Stack key={index}>
                      <Divider opacity={index > 0 ? 0.35 : 1} />
                      <ProductionSiteCard
                        productionSite={productionSite}
                        title={false}
                      />
                      {index !== supplier.productionSites.length - 1}
                    </Stack>
                  )
                })}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
    </Card>
  )
}

function ReqStats({
  contact: c,
}: {
  contact: LibraryResponses.SupplierDetails
}) {
  const { t } = useTranslation(["suppliers"])

  return (
    <Stack gap={0} w={250}>
      <Group justify="space-between">
        <Text>{t("supplier.informationRequests")}</Text>
        <Text fw={700}>
          {c.traceability.completedRequests}/{c.traceability.totalRequests}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text>{t("supplier.productionSites")}</Text>
        <Text fw={700}>
          {c.prodSiteRequestStats.completedRequests}/
          {c.prodSiteRequestStats.totalRequests}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text>{t("supplier.scopeCertificates")}</Text>
        <Text fw={700}>
          {c.scopeCertificate.completedRequests}/
          {c.scopeCertificate.totalRequests}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text>{t("supplier.transactionCertificates")}</Text>
        <Text fw={700}>
          {c.txCertificate.completedRequests}/{c.txCertificate.totalRequests}
        </Text>
      </Group>
    </Stack>
  )
}

type Action = {
  label: string
  disabled: boolean
  onClick: () => void
}

type SupplierSettingsProps = {
  actions: Action[]
}

function SupplierSettings({ actions }: SupplierSettingsProps) {
  return actions.every((action) => action.disabled) ? null : (
    <Menu shadow="md">
      <Menu.Target>
        <ActionIcon
          variant="secondary"
          icon={
            <IconNavion
              hovered={false}
              color="var(--mantine-color-primary-4)"
            />
          }
        />
      </Menu.Target>

      <Menu.Dropdown>
        {actions.map((action, index) => (
          <Menu.Item
            key={index}
            leftSection={<IconNavion hovered={false} size={25} />}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}
